import { Directive, EventEmitter, Input, OnChanges, OnDestroy, Optional, Output, Self, SimpleChanges } from '@angular/core';
import { removeJSDateLocalOffset } from '@app/shared/common/timing/date-helpers';
import { changeDateTimeTimeZoneOffset } from '@app/shared/common/timing/date-time-helpers';
import { DateTime } from 'luxon';
import { BsDatepickerDirective, BsDatepickerInlineDirective } from 'ngx-bootstrap/datepicker';
import { Subscription } from 'rxjs';

@Directive({
    selector: '[datePickerAsDateTime]',
})
export class DatePickerAsDateTimeDirective implements OnDestroy, OnChanges {
    @Input() dateTime: DateTime;
    @Output() dateTimeChange = new EventEmitter();

    @Input() timezone = 'UTC';

    subscribe: Subscription;
    lastDateTime: DateTime = null;

    private readonly _datepicker: BsDatepickerDirective | BsDatepickerInlineDirective;

    constructor(
        @Optional() @Self() bsDatepicker: BsDatepickerDirective,
        @Optional() @Self() bsDatepickerInline: BsDatepickerInlineDirective,
    ) {
        this._datepicker = bsDatepicker || bsDatepickerInline;

        this.subscribe = this._datepicker.bsValueChange.subscribe(date => {
            const dateTime = this.dateToDateTime(date);
            if (!this.isSameAsLastDateTime(dateTime)) {
                this.lastDateTime = dateTime;
                this.dateTimeChange.emit(dateTime);
            }
        });
    }

    ngOnDestroy() {
        this.subscribe.unsubscribe();
    }

    ngOnChanges({ dateTime }: SimpleChanges) {
        if (dateTime) {
            if (!this.isSameAsLastDateTime(this.dateTime)) {
                this._datepicker.bsValue = this.dateTimeToDate(this.dateTime);
            }
        }
    }

    private isSameAsLastDateTime(dateTime: DateTime) {
        if (dateTime == null) {
            return this.lastDateTime == null;
        }

        if (this.lastDateTime == null) {
            return false;
        }

        return dateTime.equals(this.lastDateTime);
    }

    private dateToDateTime(date: Date): DateTime {
        if (!date) {
            return null;
        }

        const utcDateTime = removeJSDateLocalOffset(date).startOf('day');

        if (!utcDateTime.isValid) {
            return null;
        }

        return changeDateTimeTimeZoneOffset(utcDateTime, this.timezone);
    }

    private dateTimeToDate(dateTime: DateTime): Date {
        if (!dateTime) {
            return null;
        }

        return new Date(dateTime.year, dateTime.month - 1, dateTime.day);
    }
}
